import React, { useState, useEffect } from "react";
import { Button, Form, FormGroup, Input, Label, Alert, Spinner } from "reactstrap";
import axios from "axios";
import Select, { createFilter } from "react-select";
import { API_URL_comision_multi } from "../../../constants";

import { getAuthHeaders, getCurrentUser } from "../../../utils/auth";
const IngresoGeneralForm = ({
  proyectoCode,
  proyectoName,
  proyectoMoneda,
  estudiantesIngreso,
  admin = null, // Keep as 'admin' for backward compatibility
  resetState,
  toggle
}) => {
  const [formData, setFormData] = useState({
    pk: 0,
    pago_por: 'Ingreso en General',
    iva: 0,
    retencion: 0,
    isr: 0,
    subcantidad: 0,
    cantidad: 0,
    instructor: 'ingreso en general',
    comision_multi_code: '',
    ingreso_en_general: 0,
    moneda: '',
    tipo_cambio: null,
    proyecto_code: '',
    proyecto: '',
    mod_user: ''
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const username = getCurrentUser();

    if (admin) {
      const {
        pk,
        instructor,
        moneda,
        tipo_cambio,
        proyecto,
        proyecto_code,
        comision_multi_code,
        cantidad,
        iva,
        isr,
        retencion,
        subcantidad
      } = admin;

      setFormData({
        pk: pk || 0,
        instructor: instructor || 'ingreso en general',
        moneda: moneda || '',
        tipo_cambio: tipo_cambio ?? null,
        proyecto: proyecto || proyectoName,
        proyecto_code: proyecto_code || proyectoCode,
        comision_multi_code: comision_multi_code || '',
        cantidad: cantidad || 0,
        iva: iva || 0,
        isr: isr || 0,
        retencion: retencion || 0,
        subcantidad: subcantidad || 0,
        ingreso_en_general: subcantidad || 0,
        pago_por: 'Ingreso en General',
        mod_user: username
      });
    } else {
      setFormData(prev => ({
        ...prev,
        proyecto_code: proyectoCode,
        proyecto: proyectoName,
        mod_user: username
      }));
    }
  }, [proyectoCode, proyectoName, admin]);

  const generateRandomString = (length) => {
    const characters = 'ABCDEFGHJKLMNOPQRSTUVWXYZ23456789';
    let random_string = '';
    for (let i = 0; i < length; i++) {
      random_string += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return random_string;
  };

  const validateForm = () => {
    const errors = {};
    const { ingreso_en_general, moneda } = formData;

    if (!ingreso_en_general || ingreso_en_general === '' || parseFloat(ingreso_en_general) === 0) {
      errors.ingreso_en_general = 'Por favor ingresa el ingreso en general';
    } else if (parseFloat(ingreso_en_general) < 0) {
      errors.ingreso_en_general = 'El ingreso no puede ser negativo';
    }

    if (!moneda || moneda === '') {
      errors.moneda = 'Por favor selecciona una moneda';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      setErrorMessage('Por favor completa todos los campos obligatorios marcados con *');
      setTimeout(() => {
        const firstError = document.querySelector('.is-invalid-custom');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = firstError.querySelector('input, select');
          if (input) input.focus();
        }
      }, 100);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    const headers = getAuthHeaders();

    try {
      // Prepare data
      const submitData = {
        instructor: formData.instructor,
        moneda: formData.moneda,
        tipo_cambio: formData.tipo_cambio,
        proyecto: formData.proyecto,
        proyecto_code: formData.proyecto_code,
        comision_multi_code: admin ? formData.comision_multi_code : generateRandomString(5),
        cantidad: parseFloat(formData.ingreso_en_general),
        iva: parseFloat(formData.iva),
        isr: parseFloat(formData.isr),
        retencion: parseFloat(formData.retencion),
        subcantidad: parseFloat(formData.ingreso_en_general),
        pago_por: formData.pago_por,
        mod_user: formData.mod_user
      };

      // Create or update
      if (admin) {
        const url = `${API_URL_comision_multi}${formData.pk}/`;
        await axios.put(url, submitData, { headers });
      } else {
        await axios.post(API_URL_comision_multi, submitData, { headers });
      }

      resetState();
      toggle();
    } catch (error) {
      console.error("Error saving Ingreso en General:", error);

      // Handle server-side validation errors
      if (error.response?.data) {
        const serverErrors = error.response.data;

        if (typeof serverErrors === 'object' && !Array.isArray(serverErrors)) {
          setValidationErrors(serverErrors);
          setErrorMessage('Error de validación. Por favor revisa los campos.');
        } else {
          setErrorMessage(typeof serverErrors === 'string' ? serverErrors : 'Error al guardar. Por favor verifica los datos.');
        }
      } else {
        setErrorMessage('Error de conexión. Por favor intenta nuevamente.');
      }
      setIsSubmitting(false);
    }
  };

  const h3Style = { color: 'blue' };

  // Filter option for react-select (search without accents)
  const filterOption = createFilter({
    ignoreAccents: true,
    ignoreCase: true,
    trim: true,
    matchFrom: "any",
  });

  // Options for Select components
  const monedaOptions = [
    { value: "MXN", label: "MXN" },
    { value: "USD", label: "USD" },
    { value: "EUR", label: "EUR" },
    { value: "COP", label: "COP" },
  ];

  const fxRowCurrency = formData.moneda || "USD";
  const fxProjectCurrency = proyectoMoneda || "USD";
  const fxCurrency = fxRowCurrency !== "USD" ? fxRowCurrency : fxProjectCurrency;
  const showTipoCambio = fxRowCurrency !== "USD" || fxProjectCurrency !== "USD";

  return (
    <div>
      <h3>{proyectoName}</h3>
      {proyectoCode && <h3 style={h3Style}>{proyectoCode.toUpperCase()}</h3>}
      <div className="form-requirements-note">
        <strong>Nota:</strong> Los campos marcados con <span className="required-asterisk">*</span> son obligatorios.
      </div>

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label for="ingreso_en_general">
            Ingreso en General<span className="required-asterisk">*</span>
          </Label>
          <Input
            type="number"
            name="ingreso_en_general"
            onChange={onChange}
            value={formData.ingreso_en_general}
            className={validationErrors.ingreso_en_general ? 'is-invalid-custom' : ''}
            step="0.01"
            placeholder="0.00"
          />
          {validationErrors.ingreso_en_general && (
            <div className="invalid-feedback-custom">{validationErrors.ingreso_en_general}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="moneda">
            Moneda<span className="required-asterisk">*</span>
          </Label>
          <Select
            options={monedaOptions}
            value={monedaOptions.find((o) => o.value === formData.moneda) || null}
            onChange={(opt) => {
              setFormData(prev => ({ ...prev, moneda: opt ? opt.value : "" }));
              setValidationErrors(prev => ({ ...prev, moneda: '' }));
            }}
            placeholder="Selecciona moneda"
            isClearable
            filterOption={filterOption}
            className={validationErrors.moneda ? 'is-invalid' : ''}
          />
          {validationErrors.moneda && (
            <div className="invalid-feedback-custom">{validationErrors.moneda}</div>
          )}
        </FormGroup>

        {showTipoCambio && (
          <FormGroup>
            <Label for="tipo_cambio">
              Tipo de Cambio ({fxCurrency} por 1 USD):
            </Label>
            <Input
              type="number"
              step="0.0001"
              min="0"
              name="tipo_cambio"
              placeholder="ej. 17 (vacío para usar la tasa mensual)"
              onChange={(e) => setFormData(prev => ({
                ...prev,
                tipo_cambio: e.target.value === "" ? null : Number(e.target.value),
              }))}
              value={formData.tipo_cambio ?? ""}
            />
            <small className="form-text text-muted">
              {fxRowCurrency !== "USD"
                ? "Opcional. Si lo capturas, esta partida se convierte a USD dividiendo entre este número. Vacío = tasa del mes de inicio del proyecto."
                : `Opcional. Si lo capturas, esta partida en USD se convierte a ${fxProjectCurrency} multiplicando por este número. Vacío = tasa del mes de inicio del proyecto.`}
            </small>
          </FormGroup>
        )}

        {errorMessage && <Alert color="danger">{errorMessage}</Alert>}
        
        <Button color="primary" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-2" /> Guardando...
            </>
          ) : (
            'Guardar'
          )}
        </Button>
      </Form>
    </div>
  );
};

export default IngresoGeneralForm;